import { createFileRoute } from "@tanstack/react-router";
import {
  useState,
  useEffect,
  Suspense,
  type ReactNode,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import {
  getCarrierDashboardKpi,
  getCarrierDashboardSummary,
  type CarrierDashboardKpi,
} from "@/lib/api/carrier-dashboard";
import {
  getDashboardKPI,
  getDashboardSummary,
  type DashboardKPI,
} from "@/lib/api/dashboard";
import {
  getDriverDashboardSummary,
  type DriverDashboardSummary,
  type DriverDashboardTrip,
} from "@/lib/api/driver-dashboard";
import { Alert, AlertContent } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { FormGrid } from "@/components/ui/form-grid";
import { ResponsiveGrid } from "@/components/ui/responsive-grid";
import { PageTitle } from "@/contexts/page-title-context";
import { EmptyState as EmptyStateUI, Text } from "@/components/ui/text";
import { KPICard } from "@/components/ui/kpi-card";
import { Icon } from "@/components/ui/icons";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { useUserEntity } from "@/contexts/user-entity-context";
import { getCurrentUser } from "@/lib/api/auth";
import {
  getBrokerDashboardSummary,
  type BrokerDashboardKpi,
} from "@/lib/api/broker-dashboard";

import {
  DashboardSection,
  KpiGridSkeleton,
  ActionQueueCards,
  ActionQueueTreeTable,
  DetailPanelRouter,
  ResolutionDialog,
  ScopeToggle,
  NegotiationListSkeleton,
  RecentLoadsTableSkeleton,
  StaggerReveal,
} from "@/components/dashboard";
import { EscalateDialog } from "@/components/shared/escalate-dialog";
import { EntityStatusBadge } from "@/components/shared/entity-status-badge";
import { hasPermission } from "@/lib/permissions";
import { useDashboardWorkspace } from "@/hooks/use-dashboard-workspace";
import { useDashboardKpi } from "@/hooks/use-dashboard-kpi";
import { queryKeys } from "@/lib/query-keys";
import { useSizeClass } from "@/hooks/use-size-class";
import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { ProgressBar } from "@/components/ui/progress-bar";
import { Link } from "@tanstack/react-router";
// ─── Shared layout for Shipper / Carrier / Broker dashboards ─────────

type DashboardRole = "Shipper" | "TransportCompany" | "Broker";

function RoleDashboard({
  role,
  testIdPrefix,
  kpiSection,
}: {
  role: DashboardRole;
  testIdPrefix: string;
  kpiSection: ReactNode;
}) {
  const { t } = useTranslation();
  const ws = useDashboardWorkspace();
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";

  const handleDismissDetail = useCallback(() => {
    ws.handleSelectItem(null);
  }, [ws]);

  return (
    <>
      <PageTitle>{t("dashboard.title")}</PageTitle>
      <VStack
        data-testid={`${testIdPrefix}-page`}
        className={
          ws.isSimplified ? "" : "lg:h-[calc(100vh-8rem)] lg:overflow-auto"
        }
      >
        <Text color="muted" weight="semibold" className="pl-3">
          {t("dashboard.portfolioHealth")}
        </Text>

        {kpiSection}

        {/* Action Queue */}
        <VStack gap="sm">
          <HStack gap="lg">
            <Text color="muted" weight="semibold" className="pl-3">
              {t("dashboard.myActionQueue")}
            </Text>
            {!ws.isSimplified || ws.mode === "team" ? (
              <ScopeToggle
                value={ws.scope}
                onChange={ws.setScope}
                showTeam={hasPermission("ViewTeamTickets")}
              />
            ) : null}
          </HStack>

          <ActionQueueCards
            role={role}
            scope={ws.scope}
            mode={ws.mode}
            activeFilter={ws.ticketTypeFilter}
            onFilterByType={ws.isSimplified ? undefined : ws.handleFilterByType}
            data-testid={`${testIdPrefix}-action-queue`}
          />
        </VStack>

        {/* Work Table + Detail Panel */}
        {!ws.isSimplified ? (
          isCompact ? (
            /* Compact: full-width table, detail context as sheet */
            <VStack
              data-testid={`${testIdPrefix}-work-area`}
              className="flex-1"
            >
              <Card
                variant="bordered"
                padding="none"
                className="flex flex-1 flex-col overflow-hidden"
              >
                <ActionQueueTreeTable
                  role={role}
                  scope={ws.scope}
                  ticketTypeFilter={ws.ticketTypeFilter}
                  selectedTicketId={ws.selectedTicketId}
                  selectedGroupId={
                    ws.selection?.group_id ?? ws.initialGroup ?? null
                  }
                  onSelectItem={ws.handleSelectItem}
                  onGroupsLoaded={ws.setGroups}
                  viewedTicketIds={ws.viewedIds}
                  refreshKey={ws.refreshKey}
                />
              </Card>
              <AdaptiveSheet
                open={!!ws.selectedTicketId || !!ws.selection?.group_id}
                onDismiss={handleDismissDetail}
                detents={["medium", "large"]}
              >
                <DetailPanelRouter
                  selection={ws.selection}
                  groups={ws.groups}
                  onRefresh={ws.handleRefresh}
                  onEscalate={(id) => void ws.handleEscalate(id)}
                  onResolve={(id) => void ws.handleResolve(id)}
                  onMarkViewed={ws.handleMarkViewed}
                  viewedTicketIds={ws.viewedIds}
                />
              </AdaptiveSheet>
            </VStack>
          ) : (
            /* eslint-disable-next-line design-tokens/no-raw-html-elements -- CSS grid layout */
            <div
              data-testid={`${testIdPrefix}-work-area`}
              className="grid flex-1 grid-cols-1 gap-6 lg:grid-cols-3 lg:min-h-[400px]"
            >
              <HStack align="stretch" className="lg:col-span-2 overflow-hidden">
                <Card
                  variant="bordered"
                  padding="none"
                  className="flex flex-1 flex-col overflow-hidden"
                >
                  <ActionQueueTreeTable
                    role={role}
                    scope={ws.scope}
                    ticketTypeFilter={ws.ticketTypeFilter}
                    selectedTicketId={ws.selectedTicketId}
                    selectedGroupId={
                      ws.selection?.group_id ?? ws.initialGroup ?? null
                    }
                    onSelectItem={ws.handleSelectItem}
                    onGroupsLoaded={ws.setGroups}
                    viewedTicketIds={ws.viewedIds}
                    refreshKey={ws.refreshKey}
                  />
                </Card>
              </HStack>
              <VStack className="lg:col-span-1 overflow-hidden">
                <DetailPanelRouter
                  selection={ws.selection}
                  groups={ws.groups}
                  onRefresh={ws.handleRefresh}
                  onEscalate={(id) => void ws.handleEscalate(id)}
                  onResolve={(id) => void ws.handleResolve(id)}
                  onMarkViewed={ws.handleMarkViewed}
                  viewedTicketIds={ws.viewedIds}
                />
              </VStack>
            </div>
          )
        ) : null}
      </VStack>

      {!ws.isSimplified && ws.escalationDialog ? (
        <EscalateDialog
          ticketId={ws.escalationDialog.ticketId}
          ticketDisplayId={ws.escalationDialog.ticketDisplayId}
          onClose={ws.closeEscalation}
          onSuccess={ws.handleEscalationSuccess}
        />
      ) : null}
      {!ws.isSimplified && ws.resolutionDialog ? (
        <ResolutionDialog
          ticketId={ws.resolutionDialog.ticketId}
          ticketDisplayId={ws.resolutionDialog.ticketDisplayId}
          actorType={ws.resolutionDialog.actorType}
          escalationChain={ws.resolutionDialog.escalationChain}
          parentTicket={ws.resolutionDialog.parentTicket}
          onClose={ws.closeResolution}
          onSuccess={ws.handleResolutionSuccess}
        />
      ) : null}
    </>
  );
}

// ─── Shipper Dashboard ───────────────────────────────────────────────

async function fetchShipperKpi(): Promise<DashboardKPI | null> {
  try {
    const summary = await getDashboardSummary();
    return summary.kpi ?? null;
  } catch (summaryError) {
    console.error(
      "Failed to fetch dashboard summary, falling back:",
      summaryError,
    );
    const kpiData = await getDashboardKPI();
    return kpiData;
  }
}

function ShipperDashboardPage() {
  const { t } = useTranslation();
  const { kpi, isLoading, errorKind, errorMessage } = useDashboardKpi(
    queryKeys.dashboardKpi("shipper"),
    fetchShipperKpi,
  );

  const formatKPIMetadata = (key: keyof DashboardKPI): string => {
    if (!kpi) return "";

    const card = kpi[key];
    switch (key) {
      case "total_loads": {
        const num = card.change?.match(/^[+-]?\d+/)?.[0] ?? "";
        return num ? `${num} ${t("dashboard.vsLastMonth")}` : "";
      }
      case "pending":
        return card.declined
          ? `${card.declined} ${t("dashboard.declined")}`
          : "";
      case "negotiations":
        return card.active_bids
          ? `${card.active_bids} ${t("dashboard.activeBids")}`
          : "";
      case "booked":
        return card.delayed ? `${card.delayed} ${t("dashboard.delayed")}` : "";
      case "in_transit":
        return card.issues ? `${card.issues} ${t("dashboard.issues")}` : "";
      case "completed":
        return card.late ? `${card.late} ${t("dashboard.late")}` : "";
      default:
        return "";
    }
  };

  const kpiSection = (
    <>
      {errorKind && (
        <Alert data-testid="dashboard-error" color="danger">
          <AlertContent icon={<Icon.Alert size="md" />}>
            <Text color="inherit">
              {errorMessage ?? t("common.errorLoadingData")}
            </Text>
          </AlertContent>
        </Alert>
      )}

      {isLoading ? (
        <KpiGridSkeleton
          compact
          labels={[
            t("dashboard.totalLoads"),
            t("dashboard.pending"),
            t("dashboard.negotiations"),
            t("dashboard.booked"),
            t("dashboard.inTransit"),
            t("dashboard.completed"),
          ]}
          icons={[
            <Icon.Loads key="loads" size="md" />,
            <Icon.ClockOutline key="pending" size="md" />,
            <Icon.Negotiations key="negotiations" size="md" />,
            <Icon.Calendar key="booked" size="md" />,
            <Icon.Trips key="transit" size="md" />,
            <Icon.CheckCircle key="completed" size="md" />,
          ]}
        />
      ) : (
        <StaggerReveal show index={0}>
          <ResponsiveGrid cols={6} gap="sm" data-testid="dashboard-kpi-grid">
            <KPICard
              compact
              testId="kpi-total-loads"
              title={t("dashboard.totalLoads")}
              value={kpi?.total_loads.count ?? 0}
              metadata={formatKPIMetadata("total_loads")}
              iconColor="blue"
              icon={<Icon.Loads size="sm" />}
            />
            <KPICard
              compact
              testId="kpi-pending"
              title={t("dashboard.pending")}
              value={kpi?.pending.count ?? 0}
              metadata={formatKPIMetadata("pending")}
              iconColor="orange"
              icon={<Icon.ClockOutline size="sm" />}
            />
            <KPICard
              compact
              testId="kpi-negotiations"
              title={t("dashboard.negotiations")}
              value={kpi?.negotiations.count ?? 0}
              metadata={formatKPIMetadata("negotiations")}
              iconColor="blue"
              icon={<Icon.Negotiations size="sm" />}
            />
            <KPICard
              compact
              testId="kpi-booked"
              title={t("dashboard.booked")}
              value={kpi?.booked.count ?? 0}
              metadata={formatKPIMetadata("booked")}
              iconColor="purple"
              icon={<Icon.Calendar size="sm" />}
            />
            <KPICard
              compact
              testId="kpi-in-transit"
              title={t("dashboard.inTransit")}
              value={kpi?.in_transit.count ?? 0}
              metadata={formatKPIMetadata("in_transit")}
              iconColor="blue"
              icon={<Icon.Trips size="sm" />}
            />
            <KPICard
              compact
              testId="kpi-completed"
              title={t("dashboard.completed")}
              value={kpi?.completed.count ?? 0}
              metadata={formatKPIMetadata("completed")}
              iconColor="green"
              icon={<Icon.CheckCircle size="sm" />}
            />
          </ResponsiveGrid>
        </StaggerReveal>
      )}
    </>
  );

  return (
    <RoleDashboard
      role="Shipper"
      testIdPrefix="dashboard"
      kpiSection={kpiSection}
    />
  );
}

// ─── Carrier Dashboard ───────────────────────────────────────────────

async function fetchCarrierKpi(): Promise<CarrierDashboardKpi | null> {
  try {
    const summary = await getCarrierDashboardSummary({ include: ["kpi"] });
    return summary.kpi ?? null;
  } catch (summaryError) {
    console.error(
      "Failed to fetch carrier dashboard summary, falling back:",
      summaryError,
    );
    const kpiData = await getCarrierDashboardKpi();
    return kpiData;
  }
}

function CarrierDashboardPage() {
  const { t } = useTranslation();
  const { kpi, isLoading, errorKind, errorMessage } = useDashboardKpi(
    queryKeys.dashboardKpi("carrier"),
    fetchCarrierKpi,
  );

  const kpiSection = (
    <>
      {errorKind ? (
        <EmptyStateUI
          data-testid="carrier-dashboard-error"
          title={t("dashboard.carrierDashboard")}
          description={errorMessage ?? t("common.errorLoadingData")}
          icon={<Icon.Alert />}
          className="py-10"
        />
      ) : isLoading ? (
        <KpiGridSkeleton
          compact
          count={4}
          labels={[
            t("dashboard.activeTrips"),
            t("dashboard.loadOpportunities"),
            t("dashboard.capacityUsage"),
            t("dashboard.delivered"),
          ]}
          icons={[
            <Icon.Trips key="active-trips" size="md" />,
            <Icon.Loads key="load-opportunities" size="md" />,
            <Icon.Truck key="capacity" size="md" />,
            <Icon.CheckCircle key="delivered" size="md" />,
          ]}
        />
      ) : !kpi ? (
        <EmptyStateUI
          data-testid="carrier-dashboard-empty"
          title={t("dashboard.carrierDashboard")}
          description={t("dashboard.noKpiData")}
          icon={<Icon.Dashboard />}
          className="py-10"
        />
      ) : (
        <StaggerReveal show index={0}>
          <ResponsiveGrid
            cols={6}
            gap="sm"
            data-testid="carrier-dashboard-kpis"
          >
            <KPICard
              compact
              testId="carrier-kpi-active-trips"
              title={t("dashboard.activeTrips")}
              value={kpi.active_trips.count}
              iconColor="blue"
              icon={<Icon.Trips size="sm" />}
            />
            <KPICard
              compact
              testId="carrier-kpi-load-opportunities"
              title={t("dashboard.loadOpportunities")}
              value={kpi.load_opportunities.count}
              iconColor="purple"
              icon={<Icon.Loads size="sm" />}
            />
            <KPICard
              compact
              testId="carrier-kpi-capacity-usage"
              title={t("dashboard.capacityUsage")}
              value={`${kpi.capacity.usage_percent}%`}
              metadata={`${kpi.capacity.engaged_trucks}/${kpi.capacity.total_trucks} ${t("dashboard.trucksEngaged")}`}
              iconColor="orange"
              icon={<Icon.Truck size="sm" />}
            />
            <KPICard
              compact
              testId="carrier-kpi-delivered"
              title={t("dashboard.delivered")}
              value={kpi.delivered.count}
              iconColor="green"
              icon={<Icon.CheckCircle size="sm" />}
            />
          </ResponsiveGrid>
        </StaggerReveal>
      )}
    </>
  );

  return (
    <RoleDashboard
      role="TransportCompany"
      testIdPrefix="carrier-dashboard"
      kpiSection={kpiSection}
    />
  );
}

// ─── Broker Dashboard ────────────────────────────────────────────────

async function fetchBrokerKpi(): Promise<BrokerDashboardKpi | null> {
  const summary = await getBrokerDashboardSummary({ include: ["kpi"] });
  return summary.kpi ?? null;
}

function BrokerDashboardPage() {
  const { t } = useTranslation();
  const { kpi, isLoading, errorKind, errorMessage } = useDashboardKpi(
    queryKeys.dashboardKpi("broker"),
    fetchBrokerKpi,
  );

  const kpiSection = (
    <>
      {errorKind && (
        <Alert data-testid="broker-dashboard-error" color="danger">
          <AlertContent icon={<Icon.Alert size="md" />}>
            <Text color="inherit">
              {errorMessage ?? t("common.errorLoadingData")}
            </Text>
          </AlertContent>
        </Alert>
      )}

      {isLoading ? (
        <KpiGridSkeleton
          compact
          count={4}
          columns={4}
          labels={[
            t("dashboard.customerShippers"),
            t("dashboard.supplierCarriers"),
            t("dashboard.activeDeals"),
            t("dashboard.liveTrips"),
          ]}
          icons={[
            <Icon.User key="customer-shippers" size="md" />,
            <Icon.Truck key="supplier-carriers" size="md" />,
            <Icon.Negotiations key="active-deals" size="md" />,
            <Icon.Trips key="live-trips" size="md" />,
          ]}
        />
      ) : (
        <StaggerReveal show index={0}>
          <ResponsiveGrid cols={4} gap="sm" data-testid="broker-dashboard-kpis">
            <KPICard
              compact
              testId="broker-kpi-customer-shippers"
              title={t("dashboard.customerShippers")}
              value={kpi?.customer_shippers.count ?? 0}
              iconColor="blue"
              icon={<Icon.User size="sm" />}
            />
            <KPICard
              compact
              testId="broker-kpi-supplier-carriers"
              title={t("dashboard.supplierCarriers")}
              value={kpi?.supplier_carriers.count ?? 0}
              iconColor="orange"
              icon={<Icon.Truck size="sm" />}
            />
            <KPICard
              compact
              testId="broker-kpi-active-deals"
              title={t("dashboard.activeDeals")}
              value={kpi?.active_deals.count ?? 0}
              iconColor="purple"
              icon={<Icon.Negotiations size="sm" />}
            />
            <KPICard
              compact
              testId="broker-kpi-live-trips"
              title={t("dashboard.liveTrips")}
              value={kpi?.live_trips.count ?? 0}
              iconColor="green"
              icon={<Icon.Trips size="sm" />}
            />
          </ResponsiveGrid>
        </StaggerReveal>
      )}
    </>
  );

  return (
    <RoleDashboard
      role="Broker"
      testIdPrefix="broker-dashboard"
      kpiSection={kpiSection}
    />
  );
}

// ─── Driver Dashboard (separate structure, not refactored) ───────────

function DriverTripCard({ trip }: { trip: DriverDashboardTrip }) {
  const origin = trip.load.origin_city.name;
  const destination = trip.load.destination_city.name;
  const truckLabel = trip.truck.model.name;
  const truckNumber = trip.truck.current_number?.number ?? "";

  return (
    <Link
      to="/trips/$id"
      params={{ id: trip.id }}
      className="block"
      data-testid={`driver-trip-card-${trip.id}`}
    >
      <Card
        variant="bordered"
        padding="md"
        className="hover:bg-[color:var(--surface-container-low)] transition-colors"
      >
        <HStack justify="between" gap="lg">
          <VStack fill truncate gap="none">
            <HStack className="mb-1">
              <Text weight="medium" truncate>
                {origin} → {destination}
              </Text>
            </HStack>
            <HStack className="text-sm text-[color:var(--on-surface-variant)]">
              <Text size="sm" color="muted">
                {truckLabel} {truckNumber}
              </Text>
              {(trip.scheduled_departure_at || trip.load_date) && (
                <>
                  <Text size="sm" color="muted">
                    ·
                  </Text>
                  <Text size="sm" color="muted">
                    {new Date(
                      trip.scheduled_departure_at ?? trip.load_date!,
                    ).toLocaleDateString()}
                  </Text>
                </>
              )}
            </HStack>
          </VStack>
          <EntityStatusBadge entityType="trip" status={trip.status} />
        </HStack>
        {trip.completed_percentage > 0 && trip.completed_percentage < 100 && (
          <VStack className="mt-2">
            <ProgressBar value={trip.completed_percentage} />
          </VStack>
        )}
      </Card>
    </Link>
  );
}

function DriverDashboardPage() {
  const { t } = useTranslation("dashboard");
  const [summary, setSummary] = useState<DriverDashboardSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorKind, setErrorKind] = useState<"load" | null>(null);

  useEffect(() => {
    async function fetchDriverDashboard() {
      try {
        setIsLoading(true);
        setErrorKind(null);
        const data = await getDriverDashboardSummary();
        setSummary(data);
      } catch (err) {
        console.error("Failed to fetch driver dashboard:", err);
        setErrorKind("load");
      } finally {
        setIsLoading(false);
      }
    }
    fetchDriverDashboard();
  }, []);

  const kpi = summary?.kpi;

  return (
    <>
      <PageTitle>{t("title")}</PageTitle>
      <VStack gap="lg" data-testid="driver-dashboard-page">
        {errorKind && (
          <Alert data-testid="driver-dashboard-error" color="danger">
            <AlertContent icon={<Icon.Alert size="md" />}>
              <Text color="inherit">{t("driverDashboard")}</Text>
            </AlertContent>
          </Alert>
        )}

        {/* KPI Cards */}
        {isLoading ? (
          <KpiGridSkeleton
            compact
            count={3}
            labels={[
              t("activeTrips"),
              t("completedThisMonth"),
              t("totalTrips"),
            ]}
            icons={[
              <Icon.Trips key="active" size="md" />,
              <Icon.CheckCircle key="completed" size="md" />,
              <Icon.Loads key="total" size="md" />,
            ]}
          />
        ) : (
          <StaggerReveal show index={0}>
            <ResponsiveGrid
              cols={3}
              gap="sm"
              data-testid="driver-dashboard-kpis"
            >
              <KPICard
                compact
                testId="driver-kpi-active"
                title={t("activeTrips")}
                value={kpi?.active.count ?? 0}
                iconColor="blue"
                icon={<Icon.Trips size="sm" />}
              />
              <KPICard
                compact
                testId="driver-kpi-completed-month"
                title={t("completedThisMonth")}
                value={kpi?.completed_this_month.count ?? 0}
                iconColor="green"
                icon={<Icon.CheckCircle size="sm" />}
              />
              <KPICard
                compact
                testId="driver-kpi-total"
                title={t("totalTrips")}
                value={kpi?.total.count ?? 0}
                iconColor="purple"
                icon={<Icon.Loads size="sm" />}
              />
            </ResponsiveGrid>
          </StaggerReveal>
        )}

        {!isLoading && summary && (
          <FormGrid cols="lg">
            {/* Current Trip */}
            <VStack gap="sm">
              <Text color="muted" weight="semibold" className="pl-1">
                {t("currentTrip")}
              </Text>
              {summary.current_trip ? (
                <DriverTripCard trip={summary.current_trip} />
              ) : (
                <Card variant="bordered" padding="md">
                  <EmptyStateUI
                    data-testid="driver-no-current-trip"
                    title={t("noCurrentTrip")}
                    icon={<Icon.Trips />}
                    className="py-6"
                  />
                </Card>
              )}
            </VStack>

            {/* Upcoming Trips */}
            <VStack gap="sm">
              <Text color="muted" weight="semibold" className="pl-1">
                {t("upcomingTrips")}
              </Text>
              {summary.upcoming_trips.length > 0 ? (
                <VStack gap="sm">
                  {summary.upcoming_trips.map((trip) => (
                    <DriverTripCard key={trip.id} trip={trip} />
                  ))}
                </VStack>
              ) : (
                <Card variant="bordered" padding="md">
                  <EmptyStateUI
                    data-testid="driver-no-upcoming-trips"
                    title={t("noUpcomingTrips")}
                    icon={<Icon.Trips />}
                    className="py-6"
                  />
                </Card>
              )}
            </VStack>

            {/* Recent Trips */}
            <VStack gap="sm" className="lg:col-span-2">
              <Text color="muted" weight="semibold" className="pl-1">
                {t("recentTrips")}
              </Text>
              {summary.recent_trips.length > 0 ? (
                <FormGrid cols="sm" gap="sm">
                  {summary.recent_trips.map((trip) => (
                    <DriverTripCard key={trip.id} trip={trip} />
                  ))}
                </FormGrid>
              ) : (
                <Card variant="bordered" padding="md">
                  <EmptyStateUI
                    data-testid="driver-no-recent-trips"
                    title={t("noRecentTrips")}
                    icon={<Icon.Trips />}
                    className="py-6"
                  />
                </Card>
              )}
            </VStack>
          </FormGrid>
        )}
      </VStack>
    </>
  );
}

// ─── Router ──────────────────────────────────────────────────────────

function DashboardPageInner() {
  const { entity: userEntity, loading } = useUserEntity();

  if (loading) {
    return (
      <VStack gap="xl" data-testid="dashboard-loading">
        <KpiGridSkeleton compact />
        <FormGrid cols="lg-3" gap="xl">
          <DashboardSection
            testId="dashboard-loading-recent-loads"
            title=""
            className="lg:col-span-2"
            isLoading
            contentClassName="min-h-[472px]"
            loadingSkeleton={<RecentLoadsTableSkeleton rows={4} headers={[]} />}
          >
            <VStack />
          </DashboardSection>
          <DashboardSection
            testId="dashboard-loading-negotiations"
            title=""
            className="lg:col-span-1"
            isLoading
            contentClassName="min-h-[472px]"
            loadingSkeleton={<NegotiationListSkeleton cards={3} />}
          >
            <VStack />
          </DashboardSection>
        </FormGrid>
      </VStack>
    );
  }

  const user = getCurrentUser();

  if (user?.account_type === "Driver") {
    return <DriverDashboardPage />;
  }

  if (userEntity?.type === "TransportCompany") {
    return <CarrierDashboardPage />;
  }

  if (userEntity?.type === "Broker") {
    return <BrokerDashboardPage />;
  }

  return <ShipperDashboardPage />;
}

function DashboardPage() {
  return (
    <Suspense
      fallback={
        <VStack gap="xl" data-testid="dashboard-loading">
          <KpiGridSkeleton compact />
        </VStack>
      }
    >
      <DashboardPageInner />
    </Suspense>
  );
}

export const Route = createFileRoute("/_app/dashboard")({
  component: DashboardPage,
});
